import {
  Icons,
  Stack,
  Text,
  Button,
  ButtonProps,
  TextProps,
  SvgIconProps,
  SxProps,
  Box,
  StackProps,
} from '@/theme/index';
import { scrollToElement } from '@/utils/scrollToElement';

const OverviewItemButton = ({
  onClick,
  sx,
  children,
  ...rest
}: ButtonProps) => (
  <Button
    variant="text"
    color="success"
    sx={{
      textDecoration: 'underline',
      ':hover': { textDecoration: 'underline', backgroundColor: 'inherit' },
      margin: 0,
      lineHeight: 1.25,
      padding: 0,
      marginLeft: { xs: '32px !important', sm: 0 },
      ...sx,
    }}
    disableRipple
    disableFocusRipple
    onClick={onClick}
    {...rest}
  >
    {children}
  </Button>
);

interface OverviewItemProps {
  /** The main text that will be displayed */
  label: string | null;
  labelStyles?: SxProps;
  /** Name of the MUI Icon to display */
  iconName: keyof typeof Icons;
  iconStyles?: SxProps;
  /** The text to display on the button */
  buttonLabel?: string;
  buttonStyles?: SxProps;
  /** The section to scroll to when the button is clicked */
  section?: string;
  otherProps?: {
    textProps?: TextProps;
    iconProps?: SvgIconProps;
    buttonProps?: ButtonProps & { 'data-testid'?: string };
    containerProps?: StackProps & { 'data-cy'?: string };
  };
  /** Any additional content to display */
  children?: React.ReactNode | React.ReactNode[];
  /** Any additional note to display */
  note?: string;
}

export function OverviewItem({
  label,
  labelStyles,
  iconName,
  iconStyles,
  buttonLabel,
  buttonStyles,
  section = '',
  otherProps,
  children,
  note,
}: OverviewItemProps) {
  const Icon = Icons[iconName];
  const { textProps, iconProps, buttonProps, containerProps } =
    otherProps ?? {};

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 2, sm: 0 }}
      alignItems={{ xs: 'flex-start' }}
      justifyContent="space-between"
      padding={1.75}
      {...containerProps}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Icon
          color="primary"
          sx={{ fontSize: '1.25rem', ...iconStyles }}
          {...iconProps}
        />
        {label && (
          <Box>
            <Text
              variant="body1"
              lineHeight={1.25}
              sx={labelStyles}
              {...textProps}
            >
              {label}
            </Text>
            {note && (
              <Text variant="caption" color="primary">
                {note}
              </Text>
            )}
          </Box>
        )}
        {children}
      </Stack>
      {buttonLabel && (
        <OverviewItemButton
          onClick={() => scrollToElement(section)}
          sx={buttonStyles}
          {...buttonProps}
        >
          {buttonLabel}
        </OverviewItemButton>
      )}
    </Stack>
  );
}
